import React, { useEffect, useMemo, useState } from 'react';
import { Info } from 'lucide-react';
import type { PlayerWithdrawalAutoApproveSnapshot } from '../../types';
import Button from '../ui/Button';
import Input from '../ui/Input';

type Level = 'shop' | 'agent' | 'super_agent';

type Props = {
  level: Level;
  disabled?: boolean;
  snapshot?: PlayerWithdrawalAutoApproveSnapshot | null;
  saving?: boolean;
  onSave: (value: number | null) => Promise<void> | void;
};

function formatUsd(n: number | null | undefined): string {
  if (n == null || !Number.isFinite(Number(n))) return '—';
  return `$${Number(n).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
}

function levelLabel(level: string): string {
  if (level === 'super_agent') return 'Super agent';
  if (level === 'platform') return 'Platform';
  if (level === 'agent') return 'Agent';
  return 'Shop';
}

function inheritHint(level: Level): string {
  if (level === 'shop') return 'Inherit from agent → super agent → platform';
  if (level === 'agent') return 'Inherit from super agent → platform';
  return 'Use platform default';
}

const AdminPlayerWithdrawalAutoApproveCard: React.FC<Props> = ({
  level,
  disabled,
  snapshot,
  saving,
  onSave,
}) => {
  const initialInherit = snapshot?.override == null;
  const [useInherit, setUseInherit] = useState(initialInherit);
  const [value, setValue] = useState(() =>
    String(snapshot?.override ?? snapshot?.effective ?? snapshot?.platformDefault ?? 200)
  );
  const [localError, setLocalError] = useState<string | null>(null);

  useEffect(() => {
    setUseInherit(snapshot?.override == null);
    setValue(String(snapshot?.override ?? snapshot?.effective ?? snapshot?.platformDefault ?? 200));
    setLocalError(null);
  }, [snapshot?.override, snapshot?.effective, snapshot?.platformDefault]);

  const chainText = useMemo(() => {
    if (!snapshot?.chain?.length) return null;
    return snapshot.chain
      .map((item) => {
        if (item.level === 'platform') return `Platform ${formatUsd(item.override)}`;
        return `${levelLabel(item.level)} ${item.override == null ? 'inherit' : formatUsd(item.override)}`;
      })
      .join(' · ');
  }, [snapshot]);

  const handleSave = async () => {
    setLocalError(null);
    if (useInherit) {
      await onSave(null);
      return;
    }
    const n = Number.parseFloat(value);
    if (!Number.isFinite(n) || n < 0) {
      setLocalError('Enter a non-negative USD amount, or inherit the parent default.');
      return;
    }
    await onSave(n);
  };

  return (
    <div className="rounded-md border p-4 space-y-4 text-sm">
      <div>
        <p className="font-medium text-foreground">Player withdrawal auto-approve (USD)</p>
        <p className="text-xs text-muted-foreground mt-1">
          Player cashouts strictly below this amount skip review (master wallet payout and game → master).
          Applies to players under this {level === 'super_agent' ? 'super agent' : level}.
        </p>
      </div>

      <div className="flex gap-2 rounded-md border border-blue-200 bg-blue-50/80 p-3 text-xs text-blue-900">
        <Info className="h-4 w-4 shrink-0 mt-0.5" aria-hidden />
        <p>
          Effective now: {formatUsd(snapshot?.effective)} ({levelLabel(snapshot?.source || 'platform')}).
          {chainText ? ` ${chainText}.` : ''}
        </p>
      </div>

      <label className="flex items-start gap-2 cursor-pointer">
        <input
          type="checkbox"
          className="mt-0.5"
          checked={useInherit}
          disabled={disabled || saving}
          onChange={(e) => setUseInherit(e.target.checked)}
        />
        <span>{inheritHint(level)}</span>
      </label>

      <div>
        <label className="block text-xs font-medium text-muted-foreground mb-1">
          Auto-approve below (USD)
        </label>
        <Input
          type="number"
          min={0}
          step="0.01"
          value={value}
          disabled={disabled || useInherit || saving}
          onChange={(e) => setValue(e.target.value)}
          placeholder="e.g. 500"
        />
        <p className="text-xs text-muted-foreground mt-1">
          Amounts at or above this value stay pending for review. 0 means every cashout needs review.
        </p>
      </div>

      {localError ? <p className="text-xs text-destructive">{localError}</p> : null}

      <Button
        type="button"
        size="sm"
        variant="outline"
        disabled={disabled || saving}
        onClick={() => void handleSave()}
      >
        {saving ? 'Saving…' : 'Save auto-approve threshold'}
      </Button>
    </div>
  );
};

export default AdminPlayerWithdrawalAutoApproveCard;
